/**
 * OwnershipNotificationSettings — "Review notifications" card, directly under
 * the publish-gate switch in the Ownership Review tab.
 *
 * Admins edit the list of addresses that are emailed when a seller's ownership
 * documents need review: removable chips, an add box (company-domain addresses
 * only, no duplicates, max 20), Save / Cancel only while the list differs from
 * what's saved, and a "Send test email" button.
 *
 * Data comes from the settings payload the gate card already loads
 * (`settings.notifications`); a save returns the full settings shape, handed
 * back through `onSettingsSaved`.
 *
 * @param {object} props
 * @param {object} props.notifications - { emails, stored_emails, default_emails,
 *   allowed_domain, delivery, updated_at, updated_by }
 * @param {(settings: object) => void} props.onSettingsSaved
 */
import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { ownershipVerificationAgent } from '../../agents/ownershipVerificationAgent';
import styles from './OwnershipNotificationSettings.module.css';

export const MAX_NOTIFICATION_EMAILS = 20;
export const NOTIFICATIONS_INTRO =
  "When a seller's ownership documents need review, we email these addresses. The email includes the listing address and why it was flagged, never the seller's name or documents.";
export const NO_RECIPIENTS_WARNING =
  "No one is notified right now. Add an address so new cases aren't missed.";
export const DELIVERY_OFF_NOTICE =
  "Email delivery isn't configured in this environment, so nothing is actually sent.";

// Deliberately simple: the server is the real validator.
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const normalize = (email) => String(email || '').trim().toLowerCase();

const sameList = (a, b) => {
  if (a.length !== b.length) return false;
  const set = new Set(a.map(normalize));
  return b.every((e) => set.has(normalize(e)));
};

const plural = (n) => `${n} ${n === 1 ? 'address' : 'addresses'}`;

/**
 * Why an address can't be added, or null when it can.
 * @param {string} raw
 * @param {string[]} current
 * @param {string} allowedDomain
 */
export function addressProblem(raw, current, allowedDomain) {
  const email = normalize(raw);
  if (!email) return 'Enter an email address.';
  if (!EMAIL_SHAPE.test(email)) return "That doesn't look like an email address.";
  const domain = normalize(allowedDomain);
  if (domain && !email.endsWith(`@${domain}`)) {
    return `Only @${domain} addresses can receive these.`;
  }
  if (current.map(normalize).includes(email)) return 'That address is already on the list.';
  if (current.length >= MAX_NOTIFICATION_EMAILS) {
    return `You can add up to ${MAX_NOTIFICATION_EMAILS} addresses.`;
  }
  return null;
}

export default function OwnershipNotificationSettings({ notifications, onSettingsSaved }) {
  const uid = useId();
  const saved = useMemo(
    () => (Array.isArray(notifications?.emails) ? notifications.emails : []),
    [notifications],
  );
  const allowedDomain = notifications?.allowed_domain || '';
  const [draft, setDraft] = useState(saved);
  const [input, setInput] = useState('');
  const [inputError, setInputError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  // Addresses the server refused on the last save (422 invalid_recipient).
  const [refused, setRefused] = useState([]);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null); // { ok: bool, text }
  const [announcement, setAnnouncement] = useState('');
  const inputRef = useRef(null);
  const chipRefs = useRef({});

  // A fresh payload (after a save, or a reload) replaces the draft.
  useEffect(() => {
    setDraft(saved);
    setRefused([]);
    setSaveError(null);
  }, [saved]);

  if (!notifications) return null;

  const dirty = !sameList(draft, saved);
  const usingDefault = notifications.stored_emails == null;
  const deliveryOff = notifications.delivery === 'mock' || notifications.delivery === 'disabled';
  const atLimit = draft.length >= MAX_NOTIFICATION_EMAILS;
  const changedBy = notifications.updated_by?.name || notifications.updated_by?.email || null;

  const addAddress = () => {
    const problem = addressProblem(input, draft, allowedDomain);
    if (problem) {
      setInputError(problem);
      return;
    }
    const email = normalize(input);
    setDraft((prev) => [...prev, email]);
    setInput('');
    setInputError(null);
    setTestResult(null);
    setAnnouncement(`${email} added. Save to apply.`);
    if (inputRef.current) inputRef.current.focus();
  };

  const removeAddress = (email, index) => {
    setDraft((prev) => prev.filter((e) => e !== email));
    setRefused((prev) => prev.filter((e) => normalize(e) !== normalize(email)));
    setTestResult(null);
    setAnnouncement(`${email} removed. Save to apply.`);
    // Keep keyboard users in the list: next chip, else previous, else the input.
    const next = draft[index + 1] || draft[index - 1];
    const target = next ? chipRefs.current[next] : inputRef.current;
    if (target) target.focus();
  };

  const handleCancel = () => {
    setDraft(saved);
    setInput('');
    setInputError(null);
    setSaveError(null);
    setRefused([]);
    setAnnouncement('Changes discarded.');
  };

  const handleSave = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    setSaveError(null);
    setRefused([]);
    setTestResult(null);
    try {
      const settings = await ownershipVerificationAgent.adminUpdateNotificationEmails(draft);
      setAnnouncement('Notification addresses saved.');
      if (onSettingsSaved) onSettingsSaved(settings);
    } catch (err) {
      console.error('Notification recipients save failed:', err?.status);
      let message;
      if (err?.detail?.code === 'invalid_recipient') {
        const invalid = Array.isArray(err.detail.invalid) ? err.detail.invalid : [];
        setRefused(invalid);
        message = invalid.length
          ? `Not saved. These addresses were refused: ${invalid.join(', ')}`
          : 'Not saved. One or more addresses were refused.';
      } else if (err?.status === 403) {
        message = 'You need admin access to change this.';
      } else {
        message = err?.message || 'The list could not be saved.';
      }
      setSaveError(message);
      setAnnouncement(message);
    } finally {
      setSaving(false);
    }
  };

  const handleSendTest = async () => {
    if (testing || dirty || saved.length === 0) return;
    setTesting(true);
    setTestResult(null);
    try {
      const result = await ownershipVerificationAgent.adminSendTestNotification();
      const count = Array.isArray(result?.sent_to) ? result.sent_to.length : 0;
      const text = `Test sent to ${plural(count)}.`;
      setTestResult({ ok: true, text });
      setAnnouncement(text);
    } catch (err) {
      console.error('Test notification failed:', err?.status);
      const text =
        err?.detail?.code === 'no_recipients' || err?.status === 409
          ? 'There is no one to send a test to. Add an address and save first.'
          : err?.message || 'The test email could not be sent.';
      setTestResult({ ok: false, text });
      setAnnouncement(text);
    } finally {
      setTesting(false);
    }
  };

  const titleId = `${uid}-title`;
  const inputId = `${uid}-add`;
  const errorId = `${uid}-add-error`;
  const isRefused = (email) => refused.map(normalize).includes(normalize(email));

  return (
    <section className={styles.card} aria-labelledby={titleId} data-testid="ownership-notifications">
      <div className={styles.visuallyHidden} aria-live="polite" aria-atomic="true" data-testid="ownership-notifications-announcer">
        {announcement}
      </div>

      <h3 id={titleId} className={styles.title}>Review notifications</h3>
      <p className={styles.intro}>{NOTIFICATIONS_INTRO}</p>

      {deliveryOff && (
        <p className={styles.notice} role="status" data-testid="ownership-notifications-delivery-off">
          {DELIVERY_OFF_NOTICE}
        </p>
      )}

      {usingDefault && !dirty && (
        <p className={styles.muted} data-testid="ownership-notifications-default">
          Using the default list (admins with an @{allowedDomain} address).
        </p>
      )}

      {draft.length === 0 ? (
        <p className={styles.warning} role="status" data-testid="ownership-notifications-empty">
          {dirty ? 'If you save an empty list, no one will be notified of new cases.' : NO_RECIPIENTS_WARNING}
        </p>
      ) : (
        <ul className={styles.chips} aria-label="Addresses that are notified">
          {draft.map((email, index) => (
            <li
              key={email}
              className={`${styles.chip} ${isRefused(email) ? styles.chipRefused : ''}`}
              data-testid="ownership-notifications-chip"
            >
              <span className={styles.chipText}>{email}</span>
              <button
                type="button"
                ref={(el) => {
                  if (el) chipRefs.current[email] = el;
                  else delete chipRefs.current[email];
                }}
                className={styles.chipRemove}
                onClick={() => removeAddress(email, index)}
                disabled={saving}
                aria-label={`Remove ${email}`}
              >
                <span aria-hidden="true">×</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className={styles.addRow}>
        <div className={styles.addField}>
          <label className={styles.label} htmlFor={inputId}>
            Add an address{allowedDomain ? ` (@${allowedDomain})` : ''}
          </label>
          <input
            id={inputId}
            ref={inputRef}
            className={styles.input}
            type="email"
            inputMode="email"
            autoComplete="off"
            value={input}
            placeholder={allowedDomain ? `name@${allowedDomain}` : 'name@example.com'}
            onChange={(e) => {
              setInput(e.target.value);
              if (inputError) setInputError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addAddress();
              }
            }}
            disabled={saving}
            aria-invalid={inputError ? 'true' : undefined}
            aria-describedby={inputError ? errorId : undefined}
            data-testid="ownership-notifications-input"
          />
        </div>
        <button
          type="button"
          className={styles.secondaryBtn}
          onClick={addAddress}
          disabled={saving || (atLimit && !input)}
          data-testid="ownership-notifications-add"
        >
          Add
        </button>
      </div>
      {inputError && (
        <p className={styles.fieldError} id={errorId} role="alert" data-testid="ownership-notifications-input-error">
          {inputError}
        </p>
      )}
      {atLimit && !inputError && (
        <p className={styles.muted}>You&apos;ve reached the limit of {MAX_NOTIFICATION_EMAILS} addresses.</p>
      )}

      {saveError && (
        <p className={styles.error} role="alert" data-testid="ownership-notifications-save-error">{saveError}</p>
      )}

      <div className={styles.actions}>
        {dirty && (
          <>
            <button
              type="button"
              className={styles.primaryBtn}
              onClick={handleSave}
              disabled={saving}
              aria-busy={saving ? 'true' : undefined}
              data-testid="ownership-notifications-save"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              className={styles.secondaryBtn}
              onClick={handleCancel}
              disabled={saving}
              data-testid="ownership-notifications-cancel"
            >
              Cancel
            </button>
          </>
        )}
        <button
          type="button"
          className={styles.secondaryBtn}
          onClick={handleSendTest}
          disabled={testing || dirty || saved.length === 0}
          aria-busy={testing ? 'true' : undefined}
          title={
            dirty
              ? 'Save your changes before sending a test.'
              : saved.length === 0
              ? 'Add an address and save before sending a test.'
              : undefined
          }
          data-testid="ownership-notifications-test"
        >
          {testing ? 'Sending…' : 'Send test email'}
        </button>
      </div>

      {testResult && (
        <p
          className={testResult.ok ? styles.success : styles.error}
          role={testResult.ok ? 'status' : 'alert'}
          data-testid="ownership-notifications-test-result"
        >
          {testResult.text}
        </p>
      )}

      {!usingDefault && changedBy && (
        <p className={styles.muted} data-testid="ownership-notifications-last-changed">
          Last changed by {changedBy}
        </p>
      )}
    </section>
  );
}
